import React, { useState, useEffect, useCallback } from 'react';
import { Card, Alert, Button, ProgressBar, Form, Row, Col, Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CustomDatePicker } from '../../components/shared/CustomDatePicker';
import {
  familyStatusOptions,
  educationLevelOptions,
  supportNetworkOptions,
  ethnicityOptions,
  financialStatusOptions,
  livingConditionsOptions,
  relationshipsOptions,
  genderOptions,
  countryCodeOptions,
  getLocalizedLabel
} from '../../components/form/FormOptions';
import { 
  RegistrationFormData, 
  saveRegistrationFormBySession, 
  completePatientRegistration,
  storeRegistrationGDPRConsent,
  getRegistrationGDPRConsent
} from '../../api/admin';
import RegistrationGDPRConsentModal from '../../components/registration/RegistrationGDPRConsentModal';
import { LanguageSwitcher } from '../../components/shared';
import './PatientRegistrationForm.css';
import './PatientRegistrationLanguage.css';

interface PatientRegistrationFormProps {
  patientId: string;
  initialData: RegistrationFormData;
  sessionId: string;
  registrationToken: string;
  initialFormProgress?: Record<string, boolean>;
  initialLanguage?: string;
}

interface RegistrationSection {
  id: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isRequired: boolean;
}

export function PatientRegistrationForm({
  patientId,
  initialData,
  sessionId,
  registrationToken,
  initialFormProgress = {},
  initialLanguage
}: PatientRegistrationFormProps) {
  const navigate = useNavigate();
  
  // Language state - set from session data (token's original language), default to Greek for patients
  const [language, setLanguage] = useState<'en' | 'el'>(
    initialLanguage === 'en' ? 'en' : 'el'
  );
  
  // Form state
  const [formData, setFormData] = useState<RegistrationFormData>(initialData as RegistrationFormData);
  
  // Track which fields have been touched by the user
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  
  // GDPR Modal state
  const [showGDPRModal, setShowGDPRModal] = useState(false);
  const [gdprConsentChecked, setGdprConsentChecked] = useState(false);
  const [gdprLoading, setGdprLoading] = useState(true);
  

  
  // Calculate the initial section based on form progress
  const calculateInitialSection = (progress: Record<string, boolean>): number => {
    const sectionOrder = ['basic', 'contact', 'family', 'medical', 'psychosocial', 'visit', 'review'];
    
    // Find the first incomplete section
    for (let i = 0; i < sectionOrder.length; i++) {
      const sectionId = sectionOrder[i];
      if (!progress[sectionId]) {
        return i;
      }
    }
    
    // If all sections are completed, go to review section
    return sectionOrder.length - 1;
  };
  
  const [currentSection, setCurrentSection] = useState(calculateInitialSection(initialFormProgress));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | 'error' | 'idle'>('idle');
  const [showAutoSaveInfo, setShowAutoSaveInfo] = useState(true);

  // Translation helper for common form labels
  const t = (key: string): string => {
    const translations: Record<string, { el: string; en: string }> = {
      // Common labels
      email: { el: 'Email', en: 'Email' },
      phone: { el: 'Τηλέφωνο', en: 'Phone' },
      mobile: { el: 'Κινητό', en: 'Mobile' },
      address: { el: 'Διεύθυνση', en: 'Address' },
      city: { el: 'Πόλη', en: 'City' },
      postalCode: { el: 'Ταχυδρομικός Κώδικας', en: 'Postal Code' },
      country: { el: 'Χώρα', en: 'Country' },
      
      // Medical labels  
      allergies: { el: 'Αλλεργίες', en: 'Allergies' },
      medications: { el: 'Φάρμακα', en: 'Medications' },
      medicalHistory: { el: 'Ιατρικό Ιστορικό', en: 'Medical History' },
      symptoms: { el: 'Συμπτώματα', en: 'Symptoms' },
      currentMedications: { el: 'Τρέχοντα Φάρμακα', en: 'Current Medications' },
      medicationHistory: { el: 'Ιστορικό Φαρμάκων', en: 'Medication History' },
      psychiatricDiagnoses: { el: 'Ψυχιατρικές Διαγνώσεις', en: 'Previous Psychiatric Diagnoses' },
      familyHistory: { el: 'Οικογενειακό Ιστορικό', en: 'Family History' },
      occupation: { el: 'Επάγγελμα', en: 'Occupation' },
      educationLevel: { el: 'Μορφωτικό Επίπεδο', en: 'Education Level' },
      familyStatus: { el: 'Οικογενειακή Κατάσταση', en: 'Family Status' },
      
      // Placeholders
      enterEmail: { el: 'Εισάγετε email', en: 'Enter email' },
      enterPhone: { el: 'Εισάγετε τηλέφωνο', en: 'Enter phone' },
      selectOption: { el: 'Επιλέξτε...', en: 'Select...' },
      optional: { el: '(προαιρετικό)', en: '(optional)' },
      required: { el: 'υποχρεωτικό', en: 'required' },
      
      // Messages
      notProvided: { el: 'Δεν παρέχεται', en: 'Not provided' },
      pleaseSelect: { el: 'Παρακαλώ επιλέξτε', en: 'Please select' },
    };
    
    return translations[key]?.[language] || key;
  };

  // Helper functions for section titles and descriptions
  const getSectionTitle = (sectionId: string, lang: 'en' | 'el'): string => {
    const titles = {
      basic: { el: 'Βασικές Πληροφορίες', en: 'Basic Information' },
      contact: { el: 'Στοιχεία Επικοινωνίας', en: 'Contact Information' },
      family: { el: 'Οικογενειακό & Κοινωνικό Περιβάλλον', en: 'Family & Social Information' },
      medical: { el: 'Ιατρικό & Ψυχιατρικό Ιστορικό', en: 'Medical & Psychiatric History' },
      psychosocial: { el: 'Ψυχοκοινωνικές Πληροφορίες', en: 'Psychosocial Information' },
      visit: { el: 'Πληροφορίες Τρέχουσας Επίσκεψης', en: 'Current Visit Information' },
      review: { el: 'Επισκόπηση & Υποβολή', en: 'Review & Submit' }
    };
    return titles[sectionId as keyof typeof titles]?.[lang] || sectionId;
  };

  const getSectionDescription = (sectionId: string, lang: 'en' | 'el'): string => {
    const descriptions = {
      basic: { 
        el: 'Τα προσωπικά σας στοιχεία (όνομα, ημερομηνία γέννησης, φύλο, κλπ.)', 
        en: 'Your personal details (name, birth date, gender, etc.)' 
      },
      contact: { 
        el: 'Τα στοιχεία επικοινωνίας σας (email, τηλέφωνα)', 
        en: 'Your contact details (email, phone numbers)' 
      },
      family: { 
        el: 'Οικογενειακή κατάσταση, εκπαίδευση και κοινωνικό υπόβαθρο', 
        en: 'Your family status, education, and social background' 
      },
      medical: { 
        el: 'Το ιατρικό και ψυχιατρικό σας υπόβαθρο', 
        en: 'Your medical and psychiatric background' 
      },
      psychosocial: { 
        el: 'Δίκτυο υποστήριξης, συνθήκες διαβίωσης και κοινωνικό υπόβαθρο', 
        en: 'Your support network, living situation, and social background' 
      },
      visit: { 
        el: 'Πληροφορίες για την τρέχουσα επίσκεψή σας και συμπτώματα (προαιρετικό)', 
        en: 'Information about your current visit and symptoms (optional)' 
      },
      review: { 
        el: 'Επισκοπήστε τις πληροφορίες σας και υποβάλετε την εγγραφή', 
        en: 'Review your information and submit your registration' 
      }
    };
    return descriptions[sectionId as keyof typeof descriptions]?.[lang] || '';
  };

  // Update section titles and descriptions when language changes
  useEffect(() => {
    setSections(prevSections => 
      prevSections.map(section => ({
        ...section,
        title: getSectionTitle(section.id, language),
        description: getSectionDescription(section.id, language)
      }))
    );
  }, [language]);

  // GDPR consent check on component mount
  useEffect(() => {
    const checkGDPRConsent = async () => {
      try {
        setGdprLoading(true);
        console.log('Checking GDPR consent for registration...');
        
        const consentStatus = await getRegistrationGDPRConsent(patientId, registrationToken);
        
        if (consentStatus) {
          console.log('GDPR consent found:', consentStatus);
          setGdprConsentChecked(true);
          
          if (!consentStatus.has_consent) {
            console.log('GDPR consent not given, showing modal');
            setShowGDPRModal(true);
          }
        } else {
          console.log('No GDPR consent found, showing modal');
          setShowGDPRModal(true);
        }
      } catch (error) {
        console.error('Error checking GDPR consent:', error);
        // On error, show modal to be safe
        setShowGDPRModal(true);
      } finally {
        setGdprLoading(false);
      }
    };

    checkGDPRConsent();
  }, [patientId, registrationToken]);

  // Define registration sections with initial progress
  const [sections, setSections] = useState<RegistrationSection[]>([
    {
      id: 'basic',
      title: getSectionTitle('basic', language),
      description: getSectionDescription('basic', language),
      isCompleted: initialFormProgress['basic'] || false,
      isRequired: true
    },
    {
      id: 'contact',
      title: getSectionTitle('contact', language),
      description: getSectionDescription('contact', language),
      isCompleted: initialFormProgress['contact'] || false,
      isRequired: true
    },
    {
      id: 'family',
      title: getSectionTitle('family', language),
      description: getSectionDescription('family', language),
      isCompleted: initialFormProgress['family'] || false,
      isRequired: false
    },
    {
      id: 'medical',
      title: getSectionTitle('medical', language),
      description: getSectionDescription('medical', language),
      isCompleted: initialFormProgress['medical'] || false,
      isRequired: false
    },
    {
      id: 'psychosocial',
      title: getSectionTitle('psychosocial', language),
      description: getSectionDescription('psychosocial', language),
      isCompleted: initialFormProgress['psychosocial'] || false,
      isRequired: false
    },
    {
      id: 'visit',
      title: getSectionTitle('visit', language),
      description: getSectionDescription('visit', language),
      isCompleted: initialFormProgress['visit'] || false,
      isRequired: false
    },
    {
      id: 'review',
      title: getSectionTitle('review', language),
      description: getSectionDescription('review', language),
      isCompleted: initialFormProgress['review'] || false,
      isRequired: true
    }
  ]);

  // Helper functions
  const getCurrentSection = (): string | undefined => {
    return sections[currentSection]?.id;
  };

  const updateSectionCompletion = (progressData: Record<string, boolean>) => {
    setSections(prevSections => 
      prevSections.map(section => ({
        ...section,
        isCompleted: progressData[section.id] || false
      }))
    );
  };

  const autoSaveFormData = async (sectionId?: string) => {
    try {
      // Silent save (no loading indicator like questionnaires)
      // setAutoSaveStatus('saving');
      
      const result = await saveRegistrationFormBySession(
        sessionId, 
        formData,
        // Use provided section or get current section
        sectionId || getCurrentSection()
      );

      if (result.success) {
        // Silent success (no indicator like questionnaires)
        // setAutoSaveStatus('saved');
        
        // Update form progress if returned
        if (result.form_progress) {
          updateSectionCompletion(result.form_progress);
        }
        
        // Keep status idle for successful saves
        setAutoSaveStatus('idle');
      } else {
        setAutoSaveStatus('error');
        // Clear error after 5 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('error');
      // Clear error after 5 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    }
  };

  const handleFieldChange = (field: keyof RegistrationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldBlur = (field: string) => {
    setTouchedFields(prev => new Set(prev).add(field));
  };

  // Validation for badge colors (shows completion only after moving past the section)
  const validateSection = (sectionId: string): boolean => {
    // Get the section index to check if it has been visited
    const sectionIndex = sections.findIndex(section => section.id === sectionId);
    const hasBeenVisited = sectionIndex <= currentSection;
    const isCurrentSection = sectionIndex === currentSection;
    
    // Current section should never show as completed (stays blue)
    // Only show as completed if the section has been visited AND we've moved past it
    if (!hasBeenVisited || isCurrentSection) {
      return false;
    }
    
    return validateSectionRequirements(sectionId);
  };

  // Validation for section requirements (used for Next button and completion logic)
  const validateSectionRequirements = (sectionId: string): boolean => {
    switch (sectionId) {
      case 'basic':
        return !!(formData.first_name && formData.last_name && formData.date_of_birth && formData.amka);
      case 'contact':
        return !!(formData.email && formData.address && formData.mobile_phone);
      case 'family':
        return true; // Optional section
      case 'medical':
        return true; // Medical section is now optional (previous_psychiatric_diagnoses is no longer required)
      case 'psychosocial':
        return true; // Optional section
      case 'visit':
        return true; // Visit section is now optional (main_complaint is no longer required)
      case 'review':
        return true; // Review section
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const currentSectionId = sections[currentSection].id;
    
    // Mark required fields as touched when trying to proceed, so validation errors show
    if (currentSectionId === 'basic') {
      setTouchedFields(prev => new Set([...Array.from(prev), 'first_name', 'last_name', 'amka']));
    } else if (currentSectionId === 'contact') {
      setTouchedFields(prev => new Set([...Array.from(prev), 'email', 'mobile_phone', 'address']));
    }
    
    // Check if current section is valid before proceeding
    if (!validateSectionRequirements(currentSectionId)) {
      // Don't proceed if validation fails - user will see the validation errors now
      return;
    }
    
    if (currentSection < sections.length - 1) {
      // Auto-save progress when moving to next section (like questionnaires)
      await autoSaveFormData(currentSectionId);
      setCurrentSection(prev => prev + 1);
      
      // Scroll to top of page smoothly after section change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      
      // Scroll to top of page smoothly after section change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const formatDateTimeForAPI = (dateString: string): string => {
    if (!dateString) return '';
    // If it's just a date (YYYY-MM-DD), add time component for API
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return `${dateString}T00:00:00`;
    }
    return dateString;
  };

  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Required field validation
    if (!formData.first_name?.trim()) {
      errors.push(language === 'el' ? 'Το όνομα είναι υποχρεωτικό' : 'First name is required');
    }
    if (!formData.last_name?.trim()) {
      errors.push(language === 'el' ? 'Το επώνυμο είναι υποχρεωτικό' : 'Last name is required');
    }
    if (!formData.date_of_birth) {
      errors.push(language === 'el' ? 'Η ημερομηνία γέννησης είναι υποχρεωτική' : 'Date of birth is required');
    }
    if (!formData.amka?.trim()) {
      errors.push(language === 'el' ? 'Το ΑΜΚΑ είναι υποχρεωτικό' : 'AMKA is required');
    }
    if (!formData.email?.trim()) {
      errors.push(language === 'el' ? 'Το email είναι υποχρεωτικό' : 'Email is required');
    } else if (!validateEmail(formData.email)) {
      errors.push(language === 'el' ? 'Το email δεν είναι έγκυρο' : 'Email is not valid');
    }
    if (!formData.address?.trim()) {
      errors.push(language === 'el' ? 'Η διεύθυνση είναι υποχρεωτική' : 'Address is required');
    }
    if (!formData.mobile_phone?.trim()) {
      errors.push(language === 'el' ? 'Το κινητό τηλέφωνο είναι υποχρεωτικό' : 'Mobile phone is required');
    }

    return { isValid: errors.length === 0, errors };
  };

  // GDPR Modal handler
  const handleGDPRConsentGiven = useCallback(async (consent: boolean) => {
    console.log('GDPR consent given:', consent);
    
    try {
      // Store GDPR consent in backend
      await storeRegistrationGDPRConsent(patientId, registrationToken, consent);
      console.log('GDPR consent stored successfully');
      
      setGdprConsentChecked(true);
      setShowGDPRModal(false);
      
      // If consent was not given, redirect to error page
      if (!consent) {
        console.log('GDPR consent not given, redirecting to error page');
        navigate('/error?type=gdpr_declined');
      }
    } catch (error) {
      console.error('Failed to store GDPR consent:', error);
      setError(language === 'el' 
        ? 'Σφάλμα κατά την αποθήκευση της συναίνεσης. Παρακαλώ δοκιμάστε ξανά.'
        : 'Error storing consent. Please try again.'
      );
    }
  }, [patientId, registrationToken, language, navigate]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError('');

      // Validate form before submission
      const validation = validateForm();
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        setIsSubmitting(false);
        return;
      }

      // Prepare data for API - format date properly
      const apiData = {
        ...formData,
        date_of_birth: formatDateTimeForAPI(formData.date_of_birth || ''),
        email: formData.email?.trim()
      };

      // Complete the registration using our session-based API
      const result = await completePatientRegistration(sessionId, apiData);

      if (result.success) {
        // Mark all sections as completed
        setSections(prevSections => 
          prevSections.map(section => ({ ...section, isCompleted: true }))
        );
        
        // Store language in sessionStorage for the completion page
        sessionStorage.setItem('registrationCompletionLanguage', language);
        
        // Navigate immediately to completion page
        navigate(`/registration-complete?lang=${language}`);
      } else {
        setError('Failed to submit registration');
      }
    } catch (error: any) {
      console.error('Registration completion error:', error);
      setError(error?.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBasicSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-person-fill me-2"></i>
        {language === 'el' ? 'Βασικές Πληροφορίες' : 'Basic Information'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Παρακαλώ παρέχετε τα προσωπικά σας στοιχεία. Τα πεδία που σημειώνονται με * είναι υποχρεωτικά.'
          : 'Please provide your personal details. Fields marked with * are required.'
        }
      </p>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Όνομα' : 'First Name'} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.first_name || ''}
              onChange={(e) => handleFieldChange('first_name', e.target.value)}
              onBlur={() => handleFieldBlur('first_name')}
              placeholder={language === 'el' ? 'Εισάγετε το όνομά σας' : 'Enter first name'}
              required
              isInvalid={touchedFields.has('first_name') && !formData.first_name?.trim()}
            />
            {touchedFields.has('first_name') && !formData.first_name?.trim() && (
              <Form.Control.Feedback type="invalid">
                {language === 'el' ? 'Το όνομα είναι υποχρεωτικό' : 'First name is required'}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Επώνυμο' : 'Last Name'} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.last_name || ''}
              onChange={(e) => handleFieldChange('last_name', e.target.value)}
              onBlur={() => handleFieldBlur('last_name')}
              placeholder={language === 'el' ? 'Εισάγετε το επώνυμό σας' : 'Enter last name'}
              required
              isInvalid={touchedFields.has('last_name') && !formData.last_name?.trim()}
            />
            {touchedFields.has('last_name') && !formData.last_name?.trim() && (
              <Form.Control.Feedback type="invalid">
                {language === 'el' ? 'Το επώνυμο είναι υποχρεωτικό' : 'Last name is required'}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Ημερομηνία Γέννησης' : 'Date of Birth'} <span className="text-danger">*</span>
            </Form.Label>
            <CustomDatePicker
              value={formData.date_of_birth || ''}
              onChange={(date) => handleFieldChange('date_of_birth', date)}
              placeholder={language === 'el' ? 'Επιλέξτε ημερομηνία γέννησης' : 'Select date of birth'}
            />
            {formData.date_of_birth === '' && (
              <Form.Text className="text-danger">
                {language === 'el' ? 'Η ημερομηνία γέννησης είναι υποχρεωτική' : 'Date of birth is required'}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Φύλο' : 'Gender'}</Form.Label>
            <Form.Select
              value={formData.gender || ''}
              onChange={(e) => handleFieldChange('gender', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε φύλο...' : 'Select gender...'}</option>
              {genderOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Εθνικότητα' : 'Ethnicity'}</Form.Label>
            <Form.Select
              value={formData.ethnicity || ''}
              onChange={(e) => handleFieldChange('ethnicity', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε εθνικότητα...' : 'Select ethnicity...'}</option>
              {ethnicityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Αριθμός Μητρώου Κοινωνικής Ασφάλισης (ΑΜΚΑ)' : 'Social Insurance Number (AMKA)'} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.amka || ''}
              onChange={(e) => handleFieldChange('amka', e.target.value)}
              onBlur={() => handleFieldBlur('amka')}
              placeholder={language === 'el' ? 'Εισάγετε ΑΜΚΑ (11 ψηφία)' : 'Enter AMKA (11 digits)'}
              maxLength={11}
              required
              isInvalid={touchedFields.has('amka') && !formData.amka?.trim()}
            />
            {touchedFields.has('amka') && !formData.amka?.trim() && (
              <Form.Control.Feedback type="invalid">
                {language === 'el' ? 'Το ΑΜΚΑ είναι υποχρεωτικό' : 'AMKA is required'}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderContactSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-telephone-fill me-2"></i>
        {language === 'el' ? 'Στοιχεία Επικοινωνίας' : 'Contact Information'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Παρακαλώ παρέχετε τα στοιχεία επικοινωνίας σας. Η διεύθυνση email είναι υποχρεωτική.'
          : 'Please provide your contact details. Email address is required.'
        }
      </p>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('email')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              onBlur={() => handleFieldBlur('email')}
              placeholder={t('enterEmail')}
              required
              isInvalid={touchedFields.has('email') && (!formData.email?.trim() || !validateEmail(formData.email))}
            />
            {touchedFields.has('email') && (!formData.email?.trim() || !validateEmail(formData.email)) && (
              <Form.Control.Feedback type="invalid">
                {language === 'el' ? 'Παρακαλώ εισάγετε έγκυρη διεύθυνση email' : 'Please enter a valid email address'}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Σταθερό Τηλέφωνο' : 'Landline Phone'}</Form.Label>
            <div className="d-flex">
              <Form.Select
                value={formData.landline_country_code || '+30'}
                onChange={(e) => handleFieldChange('landline_country_code', e.target.value)}
                className="me-2"
                style={{ width: '100px' }}
              >
                {countryCodeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                type="tel"
                value={formData.landline_phone || ''}
                onChange={(e) => handleFieldChange('landline_phone', e.target.value)}
                placeholder={language === 'el' ? 'Εισάγετε αριθμό σταθερού τηλεφώνου' : 'Enter landline phone number'}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Κινητό Τηλέφωνο' : 'Mobile Phone'} <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex">
              <Form.Select
                value={formData.mobile_country_code || '+30'}
                onChange={(e) => handleFieldChange('mobile_country_code', e.target.value)}
                className="me-2"
                style={{ width: '100px' }}
              >
                {countryCodeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                type="tel"
                value={formData.mobile_phone || ''}
                onChange={(e) => handleFieldChange('mobile_phone', e.target.value)}
                onBlur={() => handleFieldBlur('mobile_phone')}
                placeholder={language === 'el' ? 'Εισάγετε αριθμό κινητού τηλεφώνου' : 'Enter mobile phone number'}
                required
                isInvalid={touchedFields.has('mobile_phone') && !formData.mobile_phone?.trim()}
              />
            </div>
            {touchedFields.has('mobile_phone') && !formData.mobile_phone?.trim() && (
              <Form.Text className="text-danger">
                {language === 'el' ? 'Το κινητό τηλέφωνο είναι υποχρεωτικό' : 'Mobile phone is required'}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {language === 'el' ? 'Διεύθυνση' : 'Address'} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              onBlur={() => handleFieldBlur('address')}
              placeholder={language === 'el' ? 'Διεύθυνση, πόλη, ταχυδρομικός κώδικας' : 'Street address, city, postal code'}
              required
              isInvalid={touchedFields.has('address') && !formData.address?.trim()}
            />
            {touchedFields.has('address') && !formData.address?.trim() && (
              <Form.Control.Feedback type="invalid">
                {language === 'el' ? 'Η διεύθυνση είναι υποχρεωτική' : 'Address is required'}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderMedicalSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-heart-pulse-fill me-2"></i>
        {language === 'el' ? 'Ιατρικό & Ψυχιατρικό Ιστορικό' : 'Medical & Psychiatric History'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Παρακαλώ παρέχετε πληροφορίες για το ιατρικό και ψυχιατρικό σας ιστορικό. Όλες οι πληροφορίες είναι εμπιστευτικές.'
          : 'Please provide information about your medical and psychiatric history. All information is confidential.'
        }
      </p>

      <Form.Group className="mb-3">
        <Form.Label>
          {language === 'el' ? 'Προηγούμενες Ψυχιατρικές Διαγνώσεις' : 'Previous Psychiatric Diagnoses'} <span className="text-muted"></span>
        </Form.Label>
        <Form.Control 
          as="textarea" 
          rows={3} 
          value={formData.previous_psychiatric_diagnoses || ''}
          onChange={(e) => handleFieldChange('previous_psychiatric_diagnoses', e.target.value)}
          placeholder={language === 'el' ? 'Προηγούμενες διαγνώσεις ψυχικής υγείας' : 'Any previously diagnosed mental health conditions'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          {language === 'el' ? 'Ψυχιατρικές Νοσηλείες' : 'Psychiatric Hospitalizations'}
        </Form.Label>
        <Form.Control 
          as="textarea" 
          rows={3} 
          value={formData.psychiatric_hospitalizations || ''}
          onChange={(e) => handleFieldChange('psychiatric_hospitalizations', e.target.value)}
          placeholder={language === 'el' ? 'Ιστορικό νοσηλειών ψυχικής υγείας' : 'History of mental health hospitalizations'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          {language === 'el' ? 'Ιστορικό Φαρμάκων' : 'Medication History'}
        </Form.Label>
        <Form.Control 
          as="textarea" 
          rows={3} 
          value={formData.medication_history || ''}
          onChange={(e) => handleFieldChange('medication_history', e.target.value)}
          placeholder={language === 'el' ? 'Τρέχοντα και παλαιότερα ψυχιατρικά φάρμακα' : 'Current and past psychiatric medications'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          {t('allergies')}
        </Form.Label>
        <Form.Control 
          as="textarea" 
          rows={3} 
          value={formData.allergies || ''}
          onChange={(e) => handleFieldChange('allergies', e.target.value)}
          placeholder={language === 'el' ? 'Αλλεργίες σε φάρμακα και ιατρικούς περιορισμούς' : 'Drug allergies and medical restrictions'}
        />
      </Form.Group>
    </div>
  );

  const renderPsychosocialSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-people-fill me-2"></i>
        {language === 'el' ? 'Ψυχοκοινωνικές Πληροφορίες' : 'Psychosocial Information'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Αυτές οι πληροφορίες μας βοηθούν να κατανοήσουμε την κοινωνική σας κατάσταση και το δίκτυο υποστήριξής σας.'
          : 'This information helps us understand your social situation and support system.'
        }
      </p>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Δίκτυο Υποστήριξης' : 'Support Network'}</Form.Label>
            <Form.Select
              value={formData.support_network || ''}
              onChange={(e) => handleFieldChange('support_network', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε κύρια υποστήριξη...' : 'Select primary support...'}</option>
              {supportNetworkOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">{language === 'el' ? 'Κύρια πηγή συναισθηματικής υποστήριξης' : 'Primary source of emotional support'}</Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Οικονομική Κατάσταση' : 'Financial Status'}</Form.Label>
            <Form.Select
              value={formData.financial_status || ''}
              onChange={(e) => handleFieldChange('financial_status', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε οικονομική κατάσταση...' : 'Select financial status...'}</option>
              {financialStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">{language === 'el' ? 'Τρέχουσα οικονομική κατάσταση' : 'Current financial situation'}</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Συνθήκες Διαβίωσης' : 'Living Conditions'}</Form.Label>
            <Form.Select
              value={formData.living_conditions || ''}
              onChange={(e) => handleFieldChange('living_conditions', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε συνθήκες διαβίωσης...' : 'Select living conditions...'}</option>
              {livingConditionsOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">{language === 'el' ? 'Ποιότητα και ασφάλεια κατοικίας' : 'Quality and safety of housing'}</Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Σχέσεις' : 'Relationships'}</Form.Label>
            <Form.Select
              value={formData.relationships || ''}
              onChange={(e) => handleFieldChange('relationships', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε ποιότητα σχέσεων...' : 'Select relationship quality...'}</option>
              {relationshipsOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">{language === 'el' ? 'Συνολική ποιότητα προσωπικών σχέσεων' : 'Overall quality of personal relationships'}</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Ιστορικό Τραύματος' : 'Trauma History'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.trauma_history || ''}
          onChange={(e) => handleFieldChange('trauma_history', e.target.value)}
          placeholder={language === 'el' ? 'Ιστορικό κακοποίησης, τραύματος ή σημαντικών γεγονότων ζωής (εμπιστευτικό)' : 'History of abuse, trauma, or significant life events (confidential)'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Θρησκευτικές & Πνευματικές Πεποιθήσεις' : 'Religious & Spiritual Beliefs'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={formData.religious_beliefs || ''}
          onChange={(e) => handleFieldChange('religious_beliefs', e.target.value)}
          placeholder={language === 'el' ? 'Θρησκευτική προσχώρηση, πνευματικές πρακτικές ή πεποιθήσεις που μπορεί να είναι σχετικές με τη φροντίδα' : 'Religious affiliation, spiritual practices, or beliefs that may be relevant to care'}
        />
      </Form.Group>
    </div>
  );

  const renderVisitSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-clipboard-heart me-2"></i>
        {language === 'el' ? 'Πληροφορίες Τρέχουσας Επίσκεψης' : 'Current Visit Information'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Περιγράψτε τον λόγο της τρέχουσας επίσκεψής σας και τα συμπτώματα που αντιμετωπίζετε (προαιρετικό).'
          : 'Describe the reason for your current visit and symptoms you\'re experiencing (optional).'
        }
      </p>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Κύριο Αίτημα' : 'Main Complaint'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.main_complaint || ''}
          onChange={(e) => handleFieldChange('main_complaint', e.target.value)}
          placeholder={language === 'el' ? 'Κύριος λόγος αναζήτησης φροντίδας - περιγράψτε τα κύρια συμπτώματα ή ανησυχίες' : 'Primary reason for seeking care - describe main symptoms or concerns'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Διάρκεια & Ένταση Συμπτωμάτων' : 'Symptom Duration & Intensity'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.symptom_duration || ''}
          onChange={(e) => handleFieldChange('symptom_duration', e.target.value)}
          placeholder={language === 'el' ? 'Πόσο καιρό είναι παρόντα τα συμπτώματα; Πόσο σοβαρά είναι; Υπάρχουν αλλαγές με τον χρόνο;' : 'How long have symptoms been present? How severe are they? Any changes over time?'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Προκαταρκτικοί & Ανακουφιστικοί Παράγοντες' : 'Precipitating & Relieving Factors'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.precipitating_factors || ''}
          onChange={(e) => handleFieldChange('precipitating_factors', e.target.value)}
          placeholder={language === 'el' ? 'Τι κάνει τα συμπτώματα καλύτερα ή χειρότερα; Υπάρχουν γεγονότα ή καταστάσεις που τα πυροδοτούν;' : 'What makes symptoms better or worse? Any triggering events or situations?'}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{language === 'el' ? 'Πρόοδος Θεραπείας' : 'Treatment Progress'}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.treatment_progress || ''}
          onChange={(e) => handleFieldChange('treatment_progress', e.target.value)}
          placeholder={language === 'el' ? 'Προηγούμενες θεραπείες που δοκιμάστηκαν και η αποτελεσματικότητά τους. Τρέχουσα ανταπόκριση στη θεραπεία.' : 'Previous treatments tried and their effectiveness. Current treatment response.'}
        />
      </Form.Group>
    </div>
  );

  const renderFamilySection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-people-fill me-2"></i>
        {language === 'el' ? 'Οικογενειακές & Κοινωνικές Πληροφορίες' : 'Family & Social Information'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Αυτές οι πληροφορίες μας βοηθούν να παρέχουμε καλύτερη εξατομικευμένη φροντίδα.'
          : 'This information helps us provide better personalized care.'
        }
      </p>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{t('familyStatus')}</Form.Label>
            <Form.Select
              value={formData.family_status || ''}
              onChange={(e) => handleFieldChange('family_status', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε κατάσταση...' : 'Select status...'}</option>
              {familyStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Αριθμός Παιδιών' : 'Number of Children'}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="20"
              value={formData.number_of_children || ''}
              onChange={(e) => handleFieldChange('number_of_children', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Μορφωτικό Επίπεδο' : 'Education Level'}</Form.Label>
            <Form.Select
              value={formData.education_level || ''}
              onChange={(e) => handleFieldChange('education_level', e.target.value)}
            >
              <option value="">{language === 'el' ? 'Επιλέξτε μορφωτικό επίπεδο...' : 'Select education level...'}</option>
              {educationLevelOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {getLocalizedLabel(option, language)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Επάγγελμα' : 'Occupation'}</Form.Label>
            <Form.Control
              type="text"
              value={formData.occupation || ''}
              onChange={(e) => handleFieldChange('occupation', e.target.value)}
              placeholder={language === 'el' ? 'Τρέχουσα εργασία ή κατάσταση απασχόλησης' : 'Current job or employment status'}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{language === 'el' ? 'Τύπος Κατοικίας' : 'Residence Type'}</Form.Label>
            <Form.Control
              type="text"
              value={formData.residence_type || ''}
              onChange={(e) => handleFieldChange('residence_type', e.target.value)}
              placeholder={language === 'el' ? 'π.χ., Ιδιόκτητο σπίτι, Ενοίκιο, Με την οικογένεια, κλπ.' : 'e.g., Own home, Rental, With family, etc.'}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderReviewSection = () => (
    <div className="registration-section">
      <h4 className="section-title">
        <i className="bi bi-check-circle-fill me-2"></i>
        {language === 'el' ? 'Επισκόπηση & Υποβολή' : 'Review & Submit'}
      </h4>
      <p className="section-description text-muted mb-4">
        {language === 'el' 
          ? 'Παρακαλώ επισκοπήστε όλες τις πληροφορίες σας πριν υποβάλετε την εγγραφή σας.'
          : 'Please review all your information before submitting your registration.'
        }
      </p>

      <div className="review-summary">
        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>{language === 'el' ? 'Βασικές Πληροφορίες' : 'Basic Information'}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Όνομα:' : 'Name:'}</strong> {formData.first_name} {formData.last_name}<br />
                  <strong>{language === 'el' ? 'Ημερομηνία Γέννησης:' : 'Date of Birth:'}</strong> {
                    formData.date_of_birth ? 
                      new Date(formData.date_of_birth).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
                        year: 'numeric',
                        month: 'long', 
                        day: 'numeric'
                      }) : 
                      (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')
                  }<br />
                  <strong>{language === 'el' ? 'Φύλο:' : 'Gender:'}</strong> {formData.gender || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Εθνικότητα:' : 'Ethnicity:'}</strong> {formData.ethnicity || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>AMKA:</strong> {formData.amka || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="1">
            <Accordion.Header>{language === 'el' ? 'Στοιχεία Επικοινωνίας' : 'Contact Information'}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <strong>Email:</strong> {formData.email}<br />
                  <strong>{language === 'el' ? 'Σταθερό Τηλέφωνο:' : 'Landline Phone:'}</strong> {formData.landline_country_code && formData.landline_phone ? `${formData.landline_country_code} ${formData.landline_phone}` : (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Κινητό Τηλέφωνο:' : 'Mobile Phone:'}</strong> {formData.mobile_country_code && formData.mobile_phone ? `${formData.mobile_country_code} ${formData.mobile_phone}` : (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Διεύθυνση:' : 'Address:'}</strong> {formData.address || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="2">
            <Accordion.Header>{language === 'el' ? 'Οικογενειακές & Κοινωνικές Πληροφορίες' : 'Family & Social Information'}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Οικογενειακή Κατάσταση:' : 'Family Status:'}</strong> {formData.family_status || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Αριθμός Παιδιών:' : 'Number of Children:'}</strong> {formData.number_of_children || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Μορφωτικό Επίπεδο:' : 'Education Level:'}</strong> {formData.education_level || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Επάγγελμα:' : 'Occupation:'}</strong> {formData.occupation || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Τύπος Κατοικίας:' : 'Residence Type:'}</strong> {formData.residence_type || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="3">
            <Accordion.Header>{language === 'el' ? 'Ιατρικό & Ψυχιατρικό Ιστορικό' : 'Medical & Psychiatric History'}</Accordion.Header>
            <Accordion.Body>
              <strong>{language === 'el' ? 'Προηγούμενες Ψυχιατρικές Διαγνώσεις:' : 'Previous Psychiatric Diagnoses:'}</strong><br />
              <p>{formData.previous_psychiatric_diagnoses || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Ιστορικό Φαρμάκων:' : 'Medication History:'}</strong><br />
              <p>{formData.medication_history || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Αλλεργίες:' : 'Allergies:'}</strong><br />
              <p>{formData.allergies || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Χρόνιες Παθήσεις:' : 'Chronic Medical Conditions:'}</strong><br />
              <p>{formData.chronic_medical_conditions || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="4">
            <Accordion.Header>{language === 'el' ? 'Ψυχοκοινωνικές Πληροφορίες' : 'Psychosocial Information'}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Δίκτυο Υποστήριξης:' : 'Support Network:'}</strong> {formData.support_network || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Οικονομική Κατάσταση:' : 'Financial Status:'}</strong> {formData.financial_status || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Συνθήκες Διαβίωσης:' : 'Living Conditions:'}</strong> {formData.living_conditions || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
                <Col md={6}>
                  <strong>{language === 'el' ? 'Σχέσεις:' : 'Relationships:'}</strong> {formData.relationships || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Ιστορικό Τραύματος:' : 'Trauma History:'}</strong> {formData.trauma_history ? (language === 'el' ? 'Παρέχεται' : 'Provided') : (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}<br />
                  <strong>{language === 'el' ? 'Θρησκευτικές Πεποιθήσεις:' : 'Religious Beliefs:'}</strong> {formData.religious_beliefs ? (language === 'el' ? 'Παρέχεται' : 'Provided') : (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="5">
            <Accordion.Header>{language === 'el' ? 'Πληροφορίες Τρέχουσας Επίσκεψης' : 'Current Visit Information'}</Accordion.Header>
            <Accordion.Body>
              <strong>{language === 'el' ? 'Κύριο Αίτημα:' : 'Main Complaint:'}</strong><br />
              <p>{formData.main_complaint || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Διάρκεια Συμπτωμάτων:' : 'Symptom Duration:'}</strong><br />
              <p>{formData.symptom_duration || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Προκαταρκτικοί Παράγοντες:' : 'Precipitating Factors:'}</strong><br />
              <p>{formData.precipitating_factors || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
              <strong>{language === 'el' ? 'Πρόοδος Θεραπείας:' : 'Treatment Progress:'}</strong><br />
              <p>{formData.treatment_progress || (language === 'el' ? 'Δεν παρέχεται' : 'Not provided')}</p>
            </Accordion.Body>
          </Accordion.Item>
          

        </Accordion>
      </div>

      <div className="submit-confirmation mt-4">
        <div className="alert alert-info">
          <h6><i className="bi bi-info-circle me-2"></i>{language === 'el' ? 'Πριν την υποβολή:' : 'Before you submit:'}</h6>
          <ul className="mb-0">
            <li>{language === 'el' ? 'Παρακαλώ ελέγξτε προσεκτικά όλες τις πληροφορίες σας' : 'Please review all your information carefully'}</li>
            <li>{language === 'el' ? 'Βεβαιωθείτε ότι όλα τα υποχρεωτικά πεδία είναι συμπληρωμένα' : 'Make sure all required fields are completed'}</li>
            <li>{language === 'el' ? 'Μπορείτε να επιστρέψετε για να επεξεργαστείτε οποιοδήποτε τμήμα εάν χρειάζεται' : 'You can go back to edit any section if needed'}</li>
            <li>{language === 'el' ? 'Οι πληροφορίες σας θα αποθηκευτούν με ασφάλεια και θα χρησιμοποιηθούν για την ιατρική σας φροντίδα' : 'Your information will be securely stored and used for your medical care'}</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderCurrentSection = () => {
    switch (sections[currentSection].id) {
      case 'basic':
        return renderBasicSection();
      case 'contact':
        return renderContactSection();
      case 'family':
        return renderFamilySection();
      case 'medical':
        return renderMedicalSection();
      case 'psychosocial':
        return renderPsychosocialSection();
      case 'visit':
        return renderVisitSection();
      case 'review':
        return renderReviewSection();
      default:
        return null;
    }
  };

  const progress = ((currentSection + 1) / sections.length) * 100;

  // Show loading while checking GDPR consent
  if (gdprLoading) {
    return (
      <div className="patient-registration-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">
              {language === 'el' ? 'Φόρτωση...' : 'Loading...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-registration-container">
      {/* GDPR Consent Modal */}
      <RegistrationGDPRConsentModal
        isOpen={showGDPRModal}
        onClose={() => setShowGDPRModal(false)}
        onConsentGiven={handleGDPRConsentGiven}
        language={language}
      />
      
      {/* Compact header with floating language switcher */}
      <div className="compact-header mb-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="header-content">
            <h2 className="registration-title mb-1">
              <i className="bi bi-clipboard-heart me-3"></i>
              {language === 'el' ? 'Συμπληρώστε την Εγγραφή σας ως Ασθενής' : 'Complete Your Patient Registration'}
            </h2>
            {/* Auto-save status indicator - only show errors */}
            {autoSaveStatus === 'error' && (
              <div className="auto-save-status-compact">
                <span className="save-indicator error">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  <span className="d-none d-sm-inline">
                    {language === 'el' ? 'Σφάλμα αποθήκευσης' : 'Save failed - please try again'}
                  </span>
                </span>
              </div>
            )}
          </div>
          {/* Language switcher using universal component */}
          <LanguageSwitcher
            selectedLanguage={language}
            onLanguageChange={setLanguage}
            variant="compact"
            className="registration-lang-switcher"
          />
        </div>
      </div>

      {/* Progress Bar */}
      <div 
        className="progress-section mb-4"
        data-progress-text={`${currentSection + 1} ${language === 'el' ? 'από' : 'of'} ${sections.length} - ${sections[currentSection]?.title}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-bold">{language === 'el' ? 'Πρόοδος' : 'Progress'}</span>
          <span className="text-muted d-none d-md-inline">{currentSection + 1} {language === 'el' ? 'από' : 'of'} {sections.length}</span>
        </div>
        <ProgressBar now={progress} className="custom-progress" />
        <div className="section-indicators mt-2 d-none d-md-flex">
          {sections.map((section, index) => (
            <span
              key={section.id}
              className={`section-indicator ${index <= currentSection ? 'active' : ''} ${
                validateSection(section.id) ? 'completed' : ''
              }`}
            >
              {section.title}
            </span>
          ))}
        </div>
        {/* Mobile progress text - shown only on small screens */}
        <div className="d-md-none text-center mt-2">
          <small className="text-muted">
            {currentSection + 1} {language === 'el' ? 'από' : 'of'} {sections.length} - {sections[currentSection]?.title}
          </small>
        </div>
      </div>

      {/* Auto-save information banner (like questionnaires) */}
      {showAutoSaveInfo && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          <strong>
            {language === 'el' ? 'Αυτόματη Αποθήκευση: ' : 'Auto-save: '}
          </strong>
          {language === 'el' 
            ? 'Η πρόοδός σας αποθηκεύεται αυτόματα καθώς προχωράτε στις ενότητες. Μπορείτε να επιστρέψετε αργότερα και να συνεχίσετε από εκεί που σταματήσατε.'
            : 'Your progress is automatically saved as you move through sections. You can return later and continue where you left off.'
          }
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={() => setShowAutoSaveInfo(false)}
          ></button>
        </div>
      )}

      {/* Alerts */}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Main Content */}
      <Card className="registration-card">
        <Card.Body>
          {renderCurrentSection()}
        </Card.Body>
        
        <Card.Footer className="registration-footer">
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={handlePrevious}
              disabled={currentSection === 0}
            >
              <i className="bi bi-arrow-left me-2"></i>
              {language === 'el' ? 'Προηγούμενο' : 'Previous'}
            </Button>

            <div className="d-flex gap-2">
              {currentSection < sections.length - 1 ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={sections[currentSection].isRequired && !validateSectionRequirements(sections[currentSection].id)}
                >
                  {language === 'el' ? 'Επόμενο' : 'Next'}
                  <i className="bi bi-arrow-right ms-2"></i>
                </Button>
              ) : (
                <Button
                  variant="success"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ minWidth: '140px' }}
                >
                  {isSubmitting ? (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      <span>{language === 'el' ? 'Υποβολή' : 'Submitting'}</span>
                      <span className="loading-dots"></span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      {language === 'el' ? 'Ολοκλήρωση' : 'Complete'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
}